import React from 'react';
import { getAuthHeaders } from '../../globals.js';
import { VISIBILITY } from '../../coachingConfig.js';
import { mutedSt } from '../diagnostic/shared.jsx';

// Shared pieces of the coach space ("Acompañamiento"): the API call, error
// messages and the visibility label that every block of content carries.

const ERRORS = {
  // A session is done only once its start has come (src/sessionRules.js)
  'invalid_input: session_not_started': 'Esa sesión todavía no empezó: se puede marcar como realizada cuando llegue su horario. Si ocurrió antes, usá «Ocurrió antes: registrar el horario real».',
  'invalid_input: session_start_required': 'La sesión no tiene horario: agendala o, si ya ocurrió, registrá cuándo empezó.',
  'invalid_input: invalid_happened_at': 'El horario en que ocurrió no es válido.',
  person_exists: 'Ya hay una persona con ese email.',
  // Preparing the start of a service (docs/servicios/inicio.md)
  origin_fields_missing: 'Faltan los campos de origen en Airtable (Service Origins y Origin Quotes en Diagnostics, Service Origin en Coaching Processes). Pedíselos a Omni: docs/servicios/inicio.md.',
  'invalid_input: quote not accepted': 'La propuesta no está aceptada: solo una propuesta ganada prepara el inicio de un servicio.',
  'invalid_input: line not found': 'La propuesta cambió desde que abriste el panel: volvé a abrirlo.',
  'invalid_input: destination not allowed': 'Esa línea no puede iniciarse en ese módulo.',
  'invalid_input: already linked to another line': 'Ese proceso ya está vinculado a otra línea de una propuesta.',
  'invalid_input: quote without account': 'La propuesta no tiene empresa: un diagnóstico necesita una.',
  'invalid_input: diagnostic not in account': 'Ese diagnóstico no es de la empresa de la propuesta.',
  'invalid_input: closed': 'Ese proceso está cerrado.',
  'invalid_input: person has participations': 'No se puede borrar: la persona participa (o participó) en un proceso o en un diagnóstico. Su historia se conserva.',
  // Mi aprendizaje
  'invalid_input: empty course': 'Agrega al menos una lección antes de publicar.',
  'invalid_input: course not published': 'Publica el curso antes de asignarlo: se asigna siempre una versión publicada.',
  'invalid_input: file type': 'Ese formato no se acepta. Usa PDF, Word, Excel, PowerPoint, PNG, JPG o texto.',
  'invalid_input: file too large': 'El archivo pasa de 4 MB.',
  'invalid_input: upload failed': 'No se pudo subir el archivo. Vuelve a intentarlo.',
  'invalid_input: file unavailable': 'El archivo no está disponible.',
  'invalid_input: file not in course': 'Ese archivo no es de este curso.',
  'invalid_input: member not in process': 'Esa persona no está en el proceso.',
  'invalid_input: nobody to assign': 'Elige al menos una persona.',
  'invalid_input: feedback required': 'Escribe la devolución: qué ajustar.',
  'invalid_input: not submitted': 'Esa entrega ya fue revisada.',
  'invalid_input: confirmation required': 'Falta confirmar.',
  'invalid_input: invalid video url': 'El enlace del video tiene que empezar con https://.',
  'invalid_input: too many modules': 'Hasta 20 módulos por curso.',
  'invalid_input: too many lessons': 'Hasta 30 lecciones por módulo.',
  unauthorized: 'Tu sesión venció. Vuelve a iniciar sesión.',
  forbidden: 'Este espacio es solo de la coach: tu email tiene que estar en COACHING_COACH_EMAILS.',
  not_found: 'No se encontró el registro. Actualiza la página.',
  too_large: 'El contenido es demasiado grande para enviarlo.',
  schema_pending: 'Faltan las tablas de Acompañamiento en Airtable: aplica la migración (docs/coaching/configuracion.md).',
  fields_pending: 'Faltan campos nuevos en Airtable (espacio de la persona): aplica la migración (docs/coaching/configuracion.md).',
  'invalid_input: not shareable': 'Ese resultado no se puede mostrar en este espacio: tiene que estar aprobado, publicado y ser para esta persona.',
  'invalid_input: person already in process': 'Esa ficha ya participa en ese proceso.',
  slot_taken: 'Ese horario ya no está disponible o se superpone con otra sesión.',
  server_error: 'No se pudo completar. Vuelve a intentarlo en un momento.',
  'invalid_input: focus change needs confirmation': 'Cambiar el foco o las especialidades necesita un nuevo acuerdo explícito: confírmalo.',
  'invalid_input: missing note': 'Escribe una nota del nuevo acuerdo (cuándo y cómo se acordó).',
  'invalid_input: team needs individual sessions': 'Un programa de equipo siempre incluye sesiones individuales.',
  'invalid_input: individual needs one person': 'Un proceso individual es para una sola persona.',
  'invalid_input: team needs people': 'Un equipo necesita al menos dos personas.',
  'invalid_input: missing people': 'Elige al menos una persona.',
  'invalid_input: program archived': 'Ese programa está archivado.',
  'invalid_input: missing consent': 'Registra el consentimiento antes de cargar la transcripción.',
  'invalid_input: individual only': 'Las transcripciones se cargan solo en sesiones individuales.',
  'invalid_input: transcript too long': 'La transcripción es demasiado larga (máximo 200.000 caracteres).',
  'invalid_input: missing transcript': 'Pega el texto de la transcripción.',
  'invalid_input: ai unavailable': 'La IA no está configurada en este entorno (OPENAI_API_KEY).',
  'invalid_input: running': 'Ya hay una propuesta en preparación para esta sesión.',
  'invalid_input: trigger failed': 'No se pudo iniciar la propuesta. Vuelve a intentarlo.',
  'invalid_input: not draft': 'Esta propuesta ya no es un borrador.',
  'invalid_input: not approved': 'Primero aprueba la propuesta.',
  'invalid_input: missing objective': 'La propuesta necesita un objetivo.',
  'invalid_input: missing summary': 'Escribe el resumen antes de aprobarlo.',
  'invalid_input: not current': 'Cambió mientras lo editabas. Actualiza y vuelve a intentarlo.',
  'invalid_input: no group channel': 'Este proceso no tiene canal de equipo.',
  'invalid_input: missing confirmation': 'Falta confirmar.',
  'invalid_input: process_links_closed': 'Ya no se crean links de proceso: usá el acceso personal de quien contrata o lidera.',
  'invalid_input: already member': 'Esa persona ya está en el equipo.',
  'invalid_input: in the past': 'Elige una fecha futura.',
  'invalid_input: not scheduled': 'La sesión no tiene fecha.',
  'invalid_input: invalid link': 'El enlace tiene que empezar con http:// o https://.',
  'invalid_input: missing name': 'Falta el nombre.',
  'invalid_input: missing title': 'Falta el título.',
  'invalid_input: missing text': 'El texto está vacío.',
  'invalid_input: invalid email': 'El email no es válido.',
  'invalid_input: invalid timeZone': 'La zona horaria no es válida: búscala por país o ciudad.',
  'invalid_input: contact already linked': 'Ese contacto ya está vinculado con otra persona. Revisa ese vínculo antes de cambiarlo: las personas no se fusionan solas.',
};

export function errorText(data, status) {
  const code = data?.error || '';
  if (ERRORS[code]) return ERRORS[code];
  if (code.startsWith('invalid_input: too long')) return 'Un texto pasa del largo máximo.';
  if (code.startsWith('invalid_input')) return 'Hay un dato inválido. Revisa los campos y vuelve a intentarlo.';
  if (status === 403) return ERRORS.forbidden;
  return ERRORS.server_error;
}

export async function coachCall(body) {
  const res = await fetch('/api/coaching-admin', { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(body) });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) { const e = new Error(errorText(data, res.status)); e.code = data?.error || ''; e.status = res.status; e.data = data; throw e; }
  return data;
}

/** Server calls of the person picker, from Acompañamiento: search among the people already loaded, create with the duplicate check. */
export const coachingPeopleApi = (people) => {
  const fold = (x) => String(x || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  return {
    search: async (q) => { const w = fold(q).split(/\s+/).filter(Boolean); return people.filter(p => w.every(x => fold(`${p.name} ${p.email || ''}`).includes(x))).slice(0, 20); },
    create: async (person, { confirmNew } = {}) => (await coachCall({ action: 'savePerson', person: { ...person, timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone }, confirmNew })).person,
  };
};

const VIS_COLORS = {
  coach_private: ['rgba(248,113,113,0.12)', '#B4362C'],
  participant_private: ['rgba(248,113,113,0.12)', '#B4362C'],
  shared: ['rgba(96,165,250,0.12)', '#2D5F99'],
  group: ['rgba(91,191,181,0.15)', 'var(--globant-green)'],
  sponsor: ['rgba(251,191,36,0.12)', '#9A6212'],
};

/** Who can see this block. Shown next to every kind of content. */
export function Visibility({ kind, label }) {
  const v = VISIBILITY[kind];
  const [bg, color] = VIS_COLORS[kind] || VIS_COLORS.shared;
  return (
    <span title={v?.hint} style={{ fontSize: 10, padding: '2px 8px', borderRadius: 10, background: bg, color, whiteSpace: 'nowrap', fontWeight: 600 }}>
      👁 {label || v?.label}
    </span>
  );
}

/** Optional fields, grouped and closed by default. */
export function MoreOptions({ title = 'Más opciones', children, open = false }) {
  return (
    <details open={open} style={{ marginTop: 10 }}>
      <summary style={{ cursor: 'pointer', fontSize: 12, color: 'var(--globant-muted)' }}>{title}</summary>
      <div style={{ display: 'grid', gap: 10, marginTop: 10 }}>{children}</div>
    </details>
  );
}

export function Row({ children, style }) {
  return <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', ...style }}>{children}</div>;
}

export function Empty({ children }) {
  return <div style={{ ...mutedSt, padding: '8px 0' }}>{children}</div>;
}

export const goalStatusLabel = { open: 'Abierto', in_progress: 'En progreso', achieved: 'Logrado', paused: 'En pausa' };
export const commitmentStatusLabel = { open: 'Pendiente', done: 'Hecho', dropped: 'Descartado' };

/** Copies to the clipboard. Resolves true only if the browser allowed it (a refusal never shows as copied). */
export async function copy(text) {
  try { await navigator.clipboard.writeText(text); return true; } catch { return false; }
}

/** Active coaching tools of the library (titles and ids only), for the pickers. */
let toolsPromise = null;
export function useCoachingTools() {
  const [tools, setTools] = React.useState([]);
  React.useEffect(() => {
    toolsPromise = toolsPromise || fetch('/api/method-library', { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify({ action: 'list' }) })
      .then(r => (r.ok ? r.json() : { entries: [] }))
      .then(d => (d.entries || []).filter(e => e.status === 'active' && (e.appliesTo || []).some(k => k.startsWith('coaching_')))
        .map(e => ({ id: e.id, title: e.title, version: e.version, areas: e.areas || [], modality: e.modality, requiresTraining: e.requiresTraining })))
      .catch(() => { toolsPromise = null; return []; });
    let alive = true;
    toolsPromise.then(t => alive && setTools(t));
    return () => { alive = false; };
  }, []);
  return tools;
}
